// Range-tracking reducer (issue #18) — the pure state machine behind the
// History view's relative-vs-absolute refresh model.
//
// Relative ranges (1H/6H/24H/3D/7D, live) are sliding windows that track `now`:
// a poll tick advances their trailing edge. The moment the operator zooms or
// pans off that edge, tracking PAUSES so the data never moves under an
// analysis; a "jump to live" resumes it. Absolute (custom) ranges are frozen —
// no tick ever advances them.
//
// This module owns only the tracking lifecycle (kind / tracking / asOf). The
// concrete [from, to] window is derived elsewhere from asOfMs + the range span;
// the chart's own `visible` state owns the zoomed sub-window while paused.

export type RangeKind = 'relative' | 'absolute';

export interface TrackState {
  /** relative ⇒ slides with `now`; absolute ⇒ frozen. */
  kind:     RangeKind;
  /** true ⇒ following the live edge; false ⇒ paused (user zoomed/panned), or
   *  absolute (never tracks). */
  tracking: boolean;
  /** Trailing-edge timestamp currently applied. Advances only on a tick while
   *  relative + tracking. */
  asOfMs:   number;
}

export type TrackEvent =
  // A range pill / custom-range selection. Relative resets to tracking; an
  // absolute (custom) range is frozen from the start.
  | { type: 'setRange'; kind: RangeKind; nowMs: number }
  // Poll cadence. Advances the edge only when relative + tracking.
  | { type: 'tick'; nowMs: number }
  // The operator zoomed or panned off the trailing edge → pause a relative
  // range. No-op for an absolute range (already frozen).
  | { type: 'gesture' }
  // The in-chart "jump to live" pill / resume. Re-pins a relative range to the
  // live edge; no-op for absolute.
  | { type: 'jumpToLive'; nowMs: number };

export function initialTrackState(kind: RangeKind, nowMs: number): TrackState {
  return { kind, tracking: kind === 'relative', asOfMs: nowMs };
}

export function trackReducer(state: TrackState, event: TrackEvent): TrackState {
  switch (event.type) {
    case 'setRange':
      return {
        kind:     event.kind,
        tracking: event.kind === 'relative',
        asOfMs:   event.nowMs,
      };

    case 'tick':
      // Slide the edge only while a relative range is actively tracking.
      if (state.kind !== 'relative' || !state.tracking) return state;
      if (event.nowMs <= state.asOfMs) return state; // clock didn't move forward
      return { ...state, asOfMs: event.nowMs };

    case 'gesture':
      // Pause a tracking relative range; everything else is unaffected.
      if (state.kind !== 'relative' || !state.tracking) return state;
      return { ...state, tracking: false };

    case 'jumpToLive':
      // Resume + re-pin a relative range; absolute stays frozen.
      if (state.kind !== 'relative') return state;
      return { ...state, tracking: true, asOfMs: event.nowMs };
  }
}

/** True when a relative, paused range can be resumed — drives the in-chart
 *  "jump to live" pill's visibility. */
export function canJumpToLive(state: TrackState): boolean {
  return state.kind === 'relative' && !state.tracking;
}
